import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { SSEMessage } from '@/_shared/types';

type TurnStatus =
  | 'queued'
  | 'running'
  | 'waiting_input'
  | 'stop_requested'
  | 'completed'
  | 'failed'
  | 'aborted'
  | 'interrupted';
interface StopInput {
  id: string;
  turnId: string;
  clientRequestId: string;
}
interface StopResult {
  ok: true;
  turnId: string;
  status: TurnStatus;
  commandId: string | null;
}
type Target = StopInput & {
  workspaceKey: string;
  stopAccepted: boolean;
  acceptedReadCount: number | null;
  terminalSeen: boolean;
};

const TERMINAL = new Set<TurnStatus>(['completed', 'failed', 'aborted', 'interrupted']);

function mayBeMissingTurnStatusProcedure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = 'code' in error ? error.code : undefined;
  if (code === 'NOT_FOUND') return true;
  // Older routers return a plain 404 body ("No procedure matched"), which the
  // oRPC client reports as a malformed response rather than a typed RPC error.
  return (
    error instanceof Error &&
    (error.message === 'Cannot parse response body, please check the response body and content-type.' ||
      error.message === 'Invalid RPC response format.')
  );
}

function terminalStatusFromHistory(messages: SSEMessage[], turnId: string): TurnStatus | null {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (!message || !('turnId' in message) || message.turnId !== turnId) continue;
    if (message.type === 'turn_aborted') return 'aborted';
    if (message.type === 'turn_complete') return message.outcome === 'interrupted' ? 'interrupted' : 'completed';
    if (message.type === 'error') return 'failed';
  }
  return null;
}

const MAX_REMEMBERED_CONFIRMATIONS = 200;
const STOP_STILL_PENDING_MS = 30_000;
const STATUS_READ_FAILURES_BEFORE_ERROR = 5;

interface StatusRead {
  key: string;
  error: unknown;
  data: unknown;
  errorAt: number;
  dataAt: number;
  failures: number;
}

/** Count a new status read once. Returns null when this render already recorded it. */
function nextStatusRead(
  previous: StatusRead | null,
  read: Omit<StatusRead, 'failures'>,
  unlockSend: boolean,
): { read: StatusRead; statusError: boolean | null; unlock: boolean } | null {
  const sameTurn = previous?.key === read.key ? previous : null;
  // A new turn starts at one failure when the first read is already an error, and cannot unlock yet.
  if (sameTurn == null) {
    return { read: { ...read, failures: read.error != null ? 1 : 0 }, statusError: false, unlock: false };
  }

  const errorIsNew = read.error != null && (read.error !== sameTurn.error || read.errorAt > sameTurn.errorAt);
  const dataIsNew = read.data != null && (read.data !== sameTurn.data || read.dataAt > sameTurn.dataAt);
  if (!errorIsNew && !dataIsNew) return null;

  let failures = sameTurn.failures;
  let statusError: boolean | null = null;
  let unlock = false;
  if (errorIsNew) {
    failures += 1;
    if (failures >= STATUS_READ_FAILURES_BEFORE_ERROR) {
      if (unlockSend) {
        failures = 0;
        statusError = false;
        unlock = true;
      } else {
        statusError = true;
      }
    }
  } else if (dataIsNew) {
    failures = 0;
    statusError = false;
  }
  return { read: { ...read, failures }, statusError, unlock };
}

function rememberConfirmation(keys: ReadonlySet<string>, key: string): ReadonlySet<string> {
  if (keys.has(key)) return keys;
  const next = new Set(keys);
  next.add(key);
  if (next.size > MAX_REMEMBERED_CONFIRMATIONS) {
    const oldest = next.values().next().value;
    if (oldest !== undefined) next.delete(oldest);
  }
  return next;
}

function readStatusSource(data: object): 'direct' | 'legacy' | undefined {
  if (!('source' in data)) return undefined;
  const source = data.source;
  return source === 'direct' || source === 'legacy' ? source : undefined;
}

function turnKey(parts: { workspaceKey: string; id: string; turnId: string }): string {
  return `${parts.workspaceKey}:${parts.id}:${parts.turnId}`;
}

/** Hold the composer until Postgres confirms the turn ended, even if SSE ends first. */
export function useTurnStop(options: {
  workspaceKey: string;
  sessionId: string | null;
  /** The turn this page is already showing, so a reload can see `stop_requested`. */
  activeTurnId?: string | null;
  stopCall: (input: StopInput) => Promise<StopResult>;
  onConfirmed: (turnId: string, status: TurnStatus, acceptedStop: boolean) => void;
}): {
  stop: (turnId: string) => void;
  noteTerminal: (turnId: string) => void;
  noteStreamEnded: (turnId: string) => void;
  blocking: boolean;
  blockingTurnId: string | null;
  confirmedTurnId: string | null;
  terminalSeen: boolean;
  stopping: boolean;
  pending: boolean;
  statusError: boolean;
  /** `stop_requested` has lasted long enough that the owner may be gone. */
  stopStalled: boolean;
  retryStatus: () => Promise<void>;
} {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<Target | null>(null);
  const [statusError, setStatusError] = useState(false);
  const [stopStalled, setStopStalled] = useState(false);
  const consecutiveReadFailures = useRef(0);
  const [statusRead, setStatusRead] = useState<StatusRead | null>(null);
  // State, not a ref: both are read while rendering, which `react-hooks/refs` forbids for refs.
  const [lastConfirmed, setLastConfirmed] = useState<{
    workspaceKey: string;
    id: string;
    turnId: string;
    status: TurnStatus;
    acceptedStop: boolean;
  } | null>(null);
  const [confirmedKeys, setConfirmedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const notifiedConfirmation = useRef<string | null>(null);
  const stopCallRef = useRef(options.stopCall);
  const onConfirmedRef = useRef(options.onConfirmed);
  useEffect(() => {
    stopCallRef.current = options.stopCall;
  }, [options.stopCall]);
  useEffect(() => {
    onConfirmedRef.current = options.onConfirmed;
  }, [options.onConfirmed]);

  const current = target?.workspaceKey === options.workspaceKey && target.id === options.sessionId ? target : null;
  const activeTurnId =
    options.activeTurnId &&
    options.sessionId &&
    !confirmedKeys.has(
      turnKey({
        workspaceKey: options.workspaceKey,
        id: options.sessionId,
        turnId: options.activeTurnId,
      }),
    )
      ? options.activeTurnId
      : null;
  const polledTurnId = current?.turnId ?? activeTurnId;
  const mutation = useMutation({
    mutationFn: (input: Target) => stopCallRef.current(input),
    onSuccess: async (result, input) => {
      const queryKey = orpc.chat.turnStatus.queryOptions({ input: { id: input.id, turnId: input.turnId } }).queryKey;
      await queryClient.cancelQueries({ queryKey });
      const readCountBeforeAcceptance = queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0;
      setTarget((value) =>
        value?.workspaceKey === input.workspaceKey && value.id === input.id && value.turnId === input.turnId
          ? result.status === 'stop_requested' || result.status === 'aborted'
            ? { ...value, stopAccepted: true, acceptedReadCount: readCountBeforeAcceptance }
            : TERMINAL.has(result.status)
              ? value
              : {
                  ...value,
                  stopAccepted: false,
                  acceptedReadCount: null,
                  clientRequestId: crypto.randomUUID(),
                }
          : value,
      );
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Could not request Stop. Try again.');
    },
  });

  const statusQuery = useQuery({
    ...orpc.chat.turnStatus.queryOptions({
      input: polledTurnId && options.sessionId ? { id: options.sessionId, turnId: polledTurnId } : skipToken,
    }),
    queryFn:
      polledTurnId && options.sessionId
        ? async () => {
            const sessionId = options.sessionId!;
            try {
              const status = await orpc.chat.turnStatus.call({ id: sessionId, turnId: polledTurnId });
              return { ...status, source: 'direct' as const };
            } catch (error) {
              if (!mayBeMissingTurnStatusProcedure(error)) throw error;
              // ACT2-848's chat.get reads active-turn identity and saved events in one
              // database snapshot. A saved terminal event alone is not confirmation.
              const snapshot = await orpc.chat.get.call({ id: sessionId });
              if (snapshot.runtime.activeTurn?.turnId === polledTurnId) {
                return { turnId: polledTurnId, status: 'stop_requested' as const, source: 'legacy' as const };
              }
              const status = terminalStatusFromHistory(snapshot.sseMessages, polledTurnId);
              if (!status) throw error;
              return { turnId: polledTurnId, status, source: 'legacy' as const };
            }
          }
        : skipToken,
    retry: false,
    staleTime: 0,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      if (!polledTurnId) return false;
      if (query.state.data && TERMINAL.has(query.state.data.status)) return false;
      return query.state.error ? Math.min(1_000 * 2 ** Math.min(consecutiveReadFailures.current, 5), 30_000) : 1_000;
    },
  });

  // Apply the latest status read while rendering. An effect would setState again after paint
  // and cascade a second render; the callback below is the only external side effect.
  const data = statusQuery.data;
  if (
    !current &&
    activeTurnId &&
    options.sessionId &&
    data &&
    data.turnId === activeTurnId &&
    data.status === 'stop_requested' &&
    readStatusSource(data) === 'direct'
  ) {
    setTarget({
      workspaceKey: options.workspaceKey,
      id: options.sessionId,
      turnId: activeTurnId,
      clientRequestId: crypto.randomUUID(),
      stopAccepted: true,
      acceptedReadCount: null,
      terminalSeen: false,
    });
  }
  if (current && data && data.turnId === current.turnId) {
    if (data.status === 'stop_requested' && readStatusSource(data) === 'direct' && !current.stopAccepted) {
      setTarget({ ...current, stopAccepted: true });
    } else if (!TERMINAL.has(data.status)) {
      const readCount =
        queryClient.getQueryState(
          orpc.chat.turnStatus.queryOptions({ input: { id: current.id, turnId: current.turnId } }).queryKey,
        )?.dataUpdateCount ?? 0;
      // The command was rejected after acceptance. A new click must be a new request, while an
      // uncertain retry of the original request keeps its ID.
      const commandRejected =
        current.stopAccepted &&
        readStatusSource(data) === 'direct' &&
        data.status !== 'stop_requested' &&
        current.acceptedReadCount !== null &&
        readCount > current.acceptedReadCount;
      // A terminal-looking stream can end before the turn actually ends (or without a terminal
      // event at all). Keep Send blocked, but allow another Stop attempt.
      if (current.terminalSeen || commandRejected) {
        const nextRequestId = commandRejected ? crypto.randomUUID() : current.clientRequestId;
        setTarget({
          ...current,
          ...(current.terminalSeen ? { terminalSeen: false } : {}),
          ...(commandRejected ? { stopAccepted: false, acceptedReadCount: null, clientRequestId: nextRequestId } : {}),
        });
      }
    } else {
      const key = turnKey(current);
      if (!confirmedKeys.has(key)) {
        setConfirmedKeys((keys) => rememberConfirmation(keys, key));
        setLastConfirmed({
          workspaceKey: current.workspaceKey,
          id: current.id,
          turnId: current.turnId,
          status: data.status,
          acceptedStop: current.stopAccepted,
        });
        setTarget(null);
        setStatusError(false);
      }
    }
  }

  // Count failed status reads while rendering. The backoff timer reads the ref from an effect;
  // the Send lock and the error flag update here so a failed read does not setState twice.
  if (current) {
    const statusUpdate = nextStatusRead(
      statusRead,
      {
        key: turnKey(current),
        error: statusQuery.error,
        data: statusQuery.data,
        errorAt: statusQuery.errorUpdatedAt,
        dataAt: statusQuery.dataUpdatedAt,
      },
      current.terminalSeen && !current.stopAccepted,
    );
    if (statusUpdate) {
      setStatusRead(statusUpdate.read);
      if (statusUpdate.statusError !== null) setStatusError(statusUpdate.statusError);
      // A terminal event already reached the composer, and nobody accepted a Stop. Unlock Send
      // instead of showing the Stop callout while status reads keep failing.
      if (statusUpdate.unlock) setTarget(null);
    }
  }

  useEffect(() => {
    if (!lastConfirmed) return;
    const key = turnKey(lastConfirmed);
    if (notifiedConfirmation.current === key) return;
    notifiedConfirmation.current = key;
    onConfirmedRef.current(lastConfirmed.turnId, lastConfirmed.status, lastConfirmed.acceptedStop);
  }, [lastConfirmed]);

  // A disarmed stop cannot stay stalled. Clear it while rendering so the reset is not a setState in an effect.
  const stopStillPending = current?.stopAccepted === true && statusQuery.data?.status === 'stop_requested';
  if (!stopStillPending && stopStalled) setStopStalled(false);

  useEffect(() => {
    if (!stopStillPending) return;
    const timer = setTimeout(() => setStopStalled(true), STOP_STILL_PENDING_MS);
    return () => clearTimeout(timer);
  }, [stopStillPending, current?.turnId]);

  useEffect(() => {
    consecutiveReadFailures.current = statusRead?.failures ?? 0;
  }, [statusRead]);

  const stop = useCallback(
    (turnId: string) => {
      if (
        !options.sessionId ||
        (mutation.isPending &&
          mutation.variables?.workspaceKey === options.workspaceKey &&
          mutation.variables.id === options.sessionId &&
          mutation.variables.turnId === turnId)
      )
        return;
      const input =
        current?.turnId === turnId
          ? current
          : {
              workspaceKey: options.workspaceKey,
              id: options.sessionId,
              turnId,
              clientRequestId: crypto.randomUUID(),
              stopAccepted: false,
              acceptedReadCount: null,
              terminalSeen: false,
            };
      consecutiveReadFailures.current = 0;
      setStatusError(false);
      setStatusRead((read) => (read == null || read.failures === 0 ? read : { ...read, failures: 0 }));
      setTarget(input);
      mutation.mutate(input);
    },
    [options.workspaceKey, options.sessionId, current, mutation],
  );

  const trackEndedTurn = useCallback(
    (turnId: string, terminalEventSeen: boolean) => {
      if (!options.sessionId) return;
      const key = turnKey({ workspaceKey: options.workspaceKey, id: options.sessionId, turnId });
      if (confirmedKeys.has(key)) return;
      setTarget((value) => {
        if (value?.workspaceKey === options.workspaceKey && value.id === options.sessionId) {
          // A delayed event from an older turn must not replace the turn currently blocking Send.
          return value.turnId === turnId ? { ...value, terminalSeen: value.terminalSeen || terminalEventSeen } : value;
        }
        return {
          workspaceKey: options.workspaceKey,
          id: options.sessionId!,
          turnId,
          clientRequestId: crypto.randomUUID(),
          stopAccepted: false,
          acceptedReadCount: null,
          terminalSeen: terminalEventSeen,
        };
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.chat.turnStatus.queryOptions({ input: { id: options.sessionId, turnId } }).queryKey,
      });
    },
    [options.workspaceKey, options.sessionId, orpc, queryClient, confirmedKeys],
  );

  const noteTerminal = useCallback((turnId: string) => trackEndedTurn(turnId, true), [trackEndedTurn]);
  const noteStreamEnded = useCallback((turnId: string) => trackEndedTurn(turnId, false), [trackEndedTurn]);

  return {
    stop,
    noteTerminal,
    noteStreamEnded,
    blocking: current !== null,
    blockingTurnId: current?.turnId ?? null,
    confirmedTurnId:
      lastConfirmed?.workspaceKey === options.workspaceKey && lastConfirmed.id === options.sessionId
        ? lastConfirmed.turnId
        : null,
    terminalSeen: current?.terminalSeen === true,
    stopping: current?.stopAccepted === true,
    pending:
      mutation.isPending &&
      current !== null &&
      mutation.variables?.workspaceKey === current.workspaceKey &&
      mutation.variables.id === current.id &&
      mutation.variables.turnId === current.turnId,
    statusError: current !== null && statusError,
    stopStalled: current?.stopAccepted === true && stopStalled,
    retryStatus: async () => {
      setStatusError(false);
      await statusQuery.refetch();
    },
  };
}
